import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnInit,
    Output,
    QueryList,
    Renderer2,
    ViewChild,
    ViewChildren,
    ViewEncapsulation,
} from '@angular/core';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { ITextEditorLinkAttachment } from '@fi-shared/fi-text-editor/fi-text-editor';
import { TextEditorAttachmentType } from '@fi-shared/fi-text-editor/fi-text-editor-attachment/text-editor-attachment-type';
import { FiAttachmentDisplaySize, FiImageSize } from '@fi-shared/types';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { Animations } from '@fi-shared/utils/animations';
import { VimeoUnavailablePlaceholderSize } from '../fi-vimeo/fi-vimeo-enum';
import { FiAttachmentsListItemComponent } from '@fi-shared/media-bank/fi-attachments/fi-attachment-list-item/fi-attachments-list-item.component';

@Component({
    selector: 'fi-attachments-list',
    templateUrl: './fi-attachments-list.component.html',
    styleUrls: ['./fi-attachments-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    animations: [Animations.fadeHeight()],
    host: { class: 'fi-attachments-list' },
    standalone: false,
})
export class FiAttachmentsListComponent implements OnInit {
    @Input() mediaBankFileReferences: IMediaBankFileReference[] = [];
    @Input() disableMediaViewerDialog: boolean = false;
    @Input() linkAttachments: ITextEditorLinkAttachment[] = [];
    @Input() editMode = false;
    @Input() preventEditYoutubeLinks: boolean = false;
    @Input() showDownloadButton = false;
    @Input() imageSize: FiImageSize;
    @Input() viewSize: FiImageSize;
    @Input() displaySize: FiAttachmentDisplaySize;
    @Input() popupImageSize = FiImageSize.Large;
    @Input() isImgDraggable: boolean = true;
    @Input() isGridView: boolean = false;
    @Input() isNewLayout: boolean;
    @Input() isPdfFilePreviewMode: boolean;
    @Input() vimeoPreviewCovered: boolean = false;
    @Input() vimeoPlaceholderSize: VimeoUnavailablePlaceholderSize = VimeoUnavailablePlaceholderSize.Small;

    @Input() isCustomPreviewHandler: boolean;
    @Output() attachmentClick = new EventEmitter<IMediaBankFileReference>();

    @Input() isAddMediaBankButtonShown: boolean;
    @Output() addNewMediaBankButtonClick = new EventEmitter<void>();
    // resolve promise with 'false' for cancel removing mediaBankFileReference
    @Input() onRemovingMediaBankFileReference = (item: IMediaBankFileReference) => Promise.resolve(true);

    @Output() mediaBankFileReferencesChange = new EventEmitter<IMediaBankFileReference[]>();
    @Output() linkAttachmentsChange = new EventEmitter<ITextEditorLinkAttachment[]>();
    @Output() editLinkAttachment = new EventEmitter<ITextEditorLinkAttachment>();

    @ViewChildren(FiAttachmentsListItemComponent)
    attachmentsItemsListComponent: QueryList<FiAttachmentsListItemComponent>;

    @HostBinding('class.new-layout') get _isNewLayout() {
        return this.isNewLayout;
    }
    @HostBinding('class.grid-view') get _isGridView() {
        return this.isGridView;
    }
    @HostBinding('class.no-margin-attachments') @Input() isNoMarginAttachments: boolean = false;

    private readonly gridColumnCount: number = 3;

    constructor(
        private cdr: ChangeDetectorRef,
        private renderer: Renderer2,
        private elementRef: ElementRef,
    ) {}

    ngOnInit() {
        this.setColumnCount();
    }

    private setColumnCount(): void {
        if (this._isGridView && this.mediaBankFileReferences?.length < this.gridColumnCount)
            this.renderer.setStyle(this.elementRef.nativeElement, 'column-count', 1);
    }

    @HostBinding('class.has-attachments')
    get hasAttachments(): boolean {
        return !!this.mediaBankFileReferences?.length || !!this.linkAttachments?.length;
    }

    protected onAddNewMediaBankButtonClick(): void {
        this.addNewMediaBankButtonClick.emit();
    }

    protected isShowEditButton(linkAttachment: ITextEditorLinkAttachment): boolean {
        return (
            !this.preventEditYoutubeLinks &&
            this.editMode &&
            linkAttachment.LinkType === TextEditorAttachmentType.Youtube
        );
    }

    protected onRemoveMediaBankFileReference(
        $event: MouseEvent,
        mediaBankFileReference: IMediaBankFileReference,
    ): void {
        this.onRemovingMediaBankFileReference(mediaBankFileReference).then((result) => {
            if (result) {
                this.mediaBankFileReferences.splice(this.mediaBankFileReferences.indexOf(mediaBankFileReference), 1);
                this.mediaBankFileReferencesChange.emit(this.mediaBankFileReferences);
            }
        });
    }

    protected onRemoveLinkAttachment(linkAttachment: ITextEditorLinkAttachment): void {
        this.linkAttachments.splice(this.linkAttachments.indexOf(linkAttachment), 1);
        this.linkAttachmentsChange.emit(this.linkAttachments);
    }

    protected onEditLinkAttachment(linkAttachment: ITextEditorLinkAttachment): void {
        this.editLinkAttachment.emit(linkAttachment);
    }

    public emitDetectChanges(): void {
        this.attachmentsItemsListComponent.toArray().forEach((item: FiAttachmentsListItemComponent) => {
            item.setUrls();
        });
        this.cdr.markForCheck();
    }

    protected onAttachmentClick(mediaBankFileReference: IMediaBankFileReference): void {
        this.attachmentClick.emit(mediaBankFileReference);
    }

    protected readonly FiInfoIconMode = FiInfoIconMode;
}
